import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AuthStore from "../stores/AuthStore";
import { useObserver } from "mobx-react";
import { ProtectedRoute } from "./protected-route";
import { ProtectedAdminRoute } from "./protected-admin-route";
import { getWaitingPageStatus } from "@/data/requests/setting-requests";
import { useQuery } from "@tanstack/react-query";
import { getProfile } from "../data";

// Screens
import * as PublicScreens from "../screens";
import * as ProtectedScreens from "../screens";
import * as AdminScreens from "../screens";
import { NotFoundScreen } from "@/screens/shared/not-found";
import { HomeScreen } from "../screens/app/home";
import { TribesScreen } from "../screens/app/tribes";
import { TribeDetailScreen } from "../screens/app/tribes/detail.jsx";
import { TribePostDetailScreen } from "../screens/app/tribes/post-detail.jsx";
import { HabitDetailScreen } from "../screens/app/habits/detail.jsx";
import { TaskDetailScreen } from "../screens/app/tasks/detail.jsx";
import { RituelyAiScreen } from "../screens/app/ai-chat";
import { HabitsScreen } from "../screens/app/habits";
import { FocusScreen } from "../screens/app/focus";

const createProtectedRoute = (Component, permission = null) => (
  <ProtectedRoute>
    {permission ? (
      <ProtectedAdminRoute permission={permission}>
        <Component />
      </ProtectedAdminRoute>
    ) : (
      <Component />
    )}
  </ProtectedRoute>
);

const AuthRoutes = (
  <>
    <Route
      path="/login"
      element={
        !AuthStore.isLogin || AuthStore.isHaveTokenLogin() ? (
          <PublicScreens.LoginScreen />
        ) : (
          <Navigate to="/" />
        )
      }
    />
    <Route
      path="/register"
      element={
        !AuthStore.isLogin || AuthStore.isHaveTokenLogin() ? (
          <PublicScreens.RegisterScreen />
        ) : (
          <Navigate to="/" />
        )
      }
    />
    <Route
      path="/forgot-password"
      element={<PublicScreens.ForgotPasswordScreen />}
    />
    <Route
      path="/reset-password"
      element={<PublicScreens.PasswordResetScreen />}
    />
  </>
);

const PublicRoutes = (
  <>
    <Route exact path="/" element={<PublicScreens.LandingPageScreen />} />
    <Route path="/about" element={<PublicScreens.AboutPageScreen />} />
    <Route path="/pricing" element={<PublicScreens.PricingPageScreen />} />
    {AuthRoutes}
  </>
);

const ProtectedRoutes = (
  <>
    <Route
      path="/dashboard"
      element={createProtectedRoute(ProtectedScreens.HomeScreen)}
    />
    <Route
      path="/profile"
      element={createProtectedRoute(ProtectedScreens.ProfileScreen)}
    />
    <Route
      path="/subscription"
      element={createProtectedRoute(ProtectedScreens.SubscriptionScreen)}
    />
    <Route
      path="/settings"
      element={createProtectedRoute(ProtectedScreens.SettingsScreen)}
    />
    <Route
      path="/notifications"
      element={createProtectedRoute(ProtectedScreens.NotificationsScreen)}
    />
    <Route
      path="/plan"
      element={createProtectedRoute(ProtectedScreens.PlanScreen)}
    />
    <Route path="/chat" element={createProtectedRoute(RituelyAiScreen)} />
    <Route path="/tribes" element={createProtectedRoute(TribesScreen)} />
    <Route
      path="/tribes/:id"
      element={createProtectedRoute(TribeDetailScreen)}
    />
    <Route
      path="/tribes/:id/posts/:postId"
      element={createProtectedRoute(TribePostDetailScreen)}
    />
    <Route path="/habits" element={createProtectedRoute(HabitsScreen)} />
    <Route
      path="/habits/:id"
      element={createProtectedRoute(HabitDetailScreen)}
    />
    <Route
      path="/tasks/:id"
      element={createProtectedRoute(TaskDetailScreen)}
    />
    <Route path="/focus" element={createProtectedRoute(FocusScreen)} />
    <Route
      path="/shared"
      element={createProtectedRoute(ProtectedScreens.SharedInboxScreen)}
    />
  </>
);

const AdminRoutes = (
  <Route path="admin">
    <Route
      index
      element={createProtectedRoute(
        AdminScreens.AdminDashboardScreen,
        "ANALYTICS_READ"
      )}
    />
    <Route
      path="settings"
      element={createProtectedRoute(AdminScreens.SettingScreen, "USER_UPDATE")}
    />
    <Route
      path="users"
      element={createProtectedRoute(AdminScreens.UserListScreen, "USER_READ")}
    />
    <Route
      path="users/:id"
      element={createProtectedRoute(AdminScreens.UserManageScreen, "USER_READ")}
    />
    <Route
      path="users/new"
      element={createProtectedRoute(
        AdminScreens.UserManageScreen,
        "USER_CREATE"
      )}
    />
    <Route
      path="users/:id/history"
      element={createProtectedRoute(
        AdminScreens.UserLoginHistoryScreen,
        "USER_READ"
      )}
    />
    <Route
      path="roles"
      element={createProtectedRoute(AdminScreens.RoleListScreen, "ROLE_READ")}
    />
    <Route
      path="roles/new"
      element={createProtectedRoute(
        AdminScreens.RoleManageScreen,
        "ROLE_CREATE"
      )}
    />
    <Route
      path="roles/:id"
      element={createProtectedRoute(
        AdminScreens.RoleManageScreen,
        "ROLE_UPDATE"
      )}
    />
    <Route
      path="plans"
      element={createProtectedRoute(AdminScreens.PlanListScreen, "PLAN_READ")}
    />
    <Route
      path="plans/:id"
      element={createProtectedRoute(AdminScreens.PlanManageScreen, "PLAN_READ")}
    />
    <Route
      path="plans/new"
      element={createProtectedRoute(
        AdminScreens.PlanManageScreen,
        "PLAN_CREATE"
      )}
    />
    <Route
      path="subscriptions"
      element={createProtectedRoute(
        AdminScreens.SubscriptionListScreen,
        "SUBSCRIPTION_READ"
      )}
    />
    <Route
      path="waitinglist"
      element={createProtectedRoute(
        AdminScreens.WaitingListScreen,
        "USER_READ"
      )}
    />
    <Route
      path="audit-logs"
      element={createProtectedRoute(
        AdminScreens.AuditLogsScreen,
        "AUDIT_LOG_READ"
      )}
    />
    <Route
      path="transactions"
      element={createProtectedRoute(
        AdminScreens.TransactionListScreen,
        "TRANSACTION_READ"
      )}
    />
    <Route
      path="email-logs"
      element={createProtectedRoute(
        AdminScreens.EmailLogsScreen,
        "EMAIL_LOG_READ"
      )}
    />
    <Route
      path="notifications"
      element={createProtectedRoute(
        AdminScreens.AdminNotificationsScreen,
        "NOTIFICATION_READ"
      )}
    />
    <Route
      path="push-notifications"
      element={createProtectedRoute(
        AdminScreens.PushNotificationsScreen,
        "PUSH_NOTIFICATION_SEND"
      )}
    />
    <Route
      path="mobile/feature-flags"
      element={createProtectedRoute(
        AdminScreens.FeatureFlagsScreen,
        "FEATURE_FLAG_CREATE"
      )}
    />
    <Route
      path="mobile/remote-config"
      element={createProtectedRoute(
        AdminScreens.RemoteConfigScreen,
        "REMOTE_CONFIG_UPDATE"
      )}
    />
    <Route
      path="mobile/app-versions"
      element={createProtectedRoute(
        AdminScreens.AppVersionsScreen,
        "APP_VERSION_CREATE"
      )}
    />
    <Route
      path="mobile/devices"
      element={createProtectedRoute(AdminScreens.DevicesScreen, "DEVICE_READ")}
    />
    <Route
      path="email-templates"
      element={createProtectedRoute(
        AdminScreens.EmailTemplateListScreen,
        "USER_READ"
      )}
    />
    <Route
      path="email-templates/new"
      element={createProtectedRoute(
        AdminScreens.EmailTemplateManageScreen,
        "EMAIL_TEMPLATE_CREATE"
      )}
    />
    <Route
      path="email-templates/:id"
      element={createProtectedRoute(
        AdminScreens.EmailTemplateManageScreen,
        "EMAIL_TEMPLATE_UPDATE"
      )}
    />
  </Route>
);

export function BaseRouter() {
  const [waitingPageActive, setWaitingPageActive] = useState(false);

  const { data: profileData, isSuccess: isProfileSuccess } = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    enabled: Boolean(AuthStore.isLogin || AuthStore.isHaveTokenLogin()),
    retry: false,
  });

  useEffect(() => {
    if (isProfileSuccess && profileData?.data) {
      // Backend returns user directly, not wrapped in { user: ... }
      AuthStore.setProfile(profileData.data);
    }
  }, [isProfileSuccess, profileData]);

  useEffect(() => {
    const checkWaitingPageStatus = async () => {
      const response = await getWaitingPageStatus();
      setWaitingPageActive(response.data === "active");
    };

    checkWaitingPageStatus();
  }, []);

  return useObserver(() => (
    <Routes>
      {waitingPageActive ? (
        <>
          {AuthRoutes}
          {AdminRoutes}
          <Route exact path="*" element={<PublicScreens.WaitingPageScreen />} />
        </>
      ) : (
        <>
          {PublicRoutes}
          {ProtectedRoutes}
          {AdminRoutes}
        </>
      )}
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  ));
}
